<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30">

      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-2 text-muted">Chargement de la copie...</p>
      </div>

      <template v-else-if="sessionData">
        <!-- En-tête -->
        <div class="d-flex align-items-center justify-content-between mb-25 p-15 rounded-2 border bg-light">
          <div>
            <h5 class="fw-bold mb-0">{{ sessionData.compositionTitre }}</h5>
            <small class="text-muted">
              Étudiant : {{ sessionData.etudiant?.nom }} {{ sessionData.etudiant?.prenom }}
              <span class="mx-2">•</span>
              Code session : {{ sessionData.sessionCode }}
            </small>
          </div>
          <div class="text-end">
            <div class="fw-bold fs-20 text-primary">
              {{ totalNotes }} / {{ totalBareme }}
            </div>
            <small class="text-muted">Total actuel</small>
          </div>
        </div>

        <!-- Questions -->
        <div v-for="(q, index) in questions" :key="q.questionId" class="mb-25">
          <div class="border rounded-2 p-20">
            <!-- Numéro + contenu -->
            <div class="d-flex align-items-start gap-10 mb-15">
              <span class="badge bg-primary rounded-circle d-flex align-items-center justify-content-center"
                style="min-width:30px; height:30px; font-size:14px;">
                {{ index + 1 }}
              </span>
              <div class="flex-grow-1">
                <p class="fw-medium mb-0 fs-15">{{ q.contenu }}</p>
                <small class="text-muted">Barème : {{ q.bareme }} point(s)</small>
              </div>
            </div>

            <!-- Réponse de l'étudiant -->
            <div class="ps-40 mb-15">
              <label class="d-block text-black fw-semibold mb-2">Réponse de l'étudiant</label>
              <div class="p-15 rounded-2 bg-light border">
                <p v-if="q.reponseTexte" class="mb-0" style="white-space: pre-wrap;">{{ q.reponseTexte }}</p>
                <p v-else class="mb-0 text-muted fst-italic">Aucune réponse fournie</p>
              </div>
            </div>

            <!-- Notation -->
            <div class="ps-40 row align-items-start">
              <div class="col-md-3">
                <label class="d-block text-black fw-semibold mb-2">Note</label>
                <div class="input-group">
                  <input
                    type="number"
                    class="form-control"
                    :min="0"
                    :max="q.bareme"
                    step="0.5"
                    v-model.number="q.note"
                  />
                  <span class="input-group-text">/ {{ q.bareme }}</span>
                </div>
              </div>
              <div class="col-md-9">
                <label class="d-block text-black fw-semibold mb-2">Commentaire (optionnel)</label>
                <textarea
                  class="form-control"
                  rows="2"
                  placeholder="Remarque pour l'étudiant..."
                  v-model="q.commentaire"
                ></textarea>
              </div>
            </div>
          </div>
        </div>

        <!-- Bouton de validation -->
        <div class="text-center mt-30">
          <button
            class="btn btn-success btn-lg px-40 fw-bold"
            @click="confirmerCorrection"
            :disabled="soumissionEnCours"
          >
            <span v-if="soumissionEnCours">
              <span class="spinner-border spinner-border-sm me-5"></span>
              Enregistrement...
            </span>
            <span v-else>
              <i class="fa fa-check me-8"></i>
              Valider la correction
            </span>
          </button>
        </div>
      </template>

    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import Swal from "sweetalert2";
import ApiService from "@/services/ApiService";
import { error, success } from "@/utils/utils";

interface QuestionCorrection {
  questionId: number;
  contenu: string;
  bareme: number;
  reponseTexte: string | null;
  note: number | null;
  commentaire: string | null;
}

export default defineComponent({
  name: "CorrectionSessionPage",
  setup() {
    const route = useRoute();
    const router = useRouter();

    const loading = ref(true);
    const soumissionEnCours = ref(false);
    const sessionData = ref<any>(null);
    const questions = ref<QuestionCorrection[]>([]);

    const totalBareme = computed(() =>
      questions.value.reduce((sum, q) => sum + (q.bareme || 0), 0)
    );

    const totalNotes = computed(() =>
      questions.value.reduce((sum, q) => sum + (Number(q.note) || 0), 0)
    );

    const fetchSession = async () => {
      loading.value = true;
      try {
        const sessionId = route.params.sessionId;
        const { data } = await ApiService.get(`/sessions/${sessionId}/correction`);
        sessionData.value = data.data;
        questions.value = (data.data.questions || []).map((q: any) => ({
          ...q,
          note: q.note ?? 0,
          commentaire: q.commentaire ?? "",
        }));
      } catch (err: any) {
        error(err.response?.data?.message || "Impossible de charger la copie");
      } finally {
        loading.value = false;
      }
    };

    const confirmerCorrection = async () => {
      // Vérifier que toutes les notes sont valides
      const notesInvalides = questions.value.some(
        q => q.note === null || q.note === undefined || q.note < 0 || q.note > q.bareme
      );

      if (notesInvalides) {
        error("Veuillez vérifier les notes : elles doivent être comprises entre 0 et le barème de chaque question.");
        return;
      }

      const result = await Swal.fire({
        title: 'Valider la correction',
        html: `<p>Total : <strong>${totalNotes.value} / ${totalBareme.value}</strong></p><p>Confirmez-vous cette correction ?</p>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Oui, valider',
        cancelButtonText: 'Annuler',
        confirmButtonColor: '#28a745',
      });

      if (!result.isConfirmed) return;

      soumissionEnCours.value = true;
      try {
        const sessionId = route.params.sessionId;
        const notes = questions.value.map(q => ({
          questionId: q.questionId,
          note: q.note,
          commentaire: q.commentaire?.trim() || undefined,
        }));

        await ApiService.post(`/sessions/${sessionId}/correction`, { notes });

        success("Correction enregistrée avec succès !");
        router.push({ name: 'SessionsACorrigerPage' });
      } catch (err: any) {
        error(err.response?.data?.message || "Erreur lors de l'enregistrement de la correction");
      } finally {
        soumissionEnCours.value = false;
      }
    };

    onMounted(() => {
      fetchSession();
    });

    return {
      loading,
      soumissionEnCours,
      sessionData,
      questions,
      totalBareme,
      totalNotes,
      confirmerCorrection,
    };
  },
});
</script>

<style scoped>
.gap-10 {
  gap: 10px;
}
</style>